import React, { useCallback, useMemo, useContext } from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import { SgDateUtil, SgNumberUtil, SgSptTripUtils } from 'saga-modules';
import SgIpidUtil from 'sg-ipid-util';
import SgIpidDetails from 'sg-ipid-details';
import CommonConstants from 'sg-common-constants';
import SgSptConstants from 'sg-spt-constants';
import IpidConfig from './SgSptIpidConfig.json';
import messages from './SgSptIpid.messages';
import metadata from './SgSptIpid.metadata.json5';

function SgSptIpid(props) {
    const {
        model
    } = props;

    const sptIpidModel = { value: model };
    const translator = useContext(TranslatorContext);
    const selectedTrip = SgSptTripUtils.getSelectedTrip(sptIpidModel);
    const isSelectedTripSingle = SgSptTripUtils.isSelectedTripSingle(sptIpidModel);
    const selectedTripIndex = SgSptTripUtils.getSelectedTripIndex(sptIpidModel);
    const returnDateDtoSingle = _.get(model, `lobData.privateTravel.coverables.sptTrips.[${selectedTripIndex}].returnDate`);
    const tripCostValue = selectedTrip.tripValue_Ext;
    const coverStartDate = _.get(model, 'baseData.periodStartDate');
    const cancellationCoverNeeded = _.get(model, `lobData.privateTravel.coverables.sptTrips.[${selectedTripIndex}].isCancelCovNeeded`);
    const coverEndDateAnnual = SgDateUtil.addDaysToDateDTO(-1, _.get(model, 'baseData.periodEndDate'));
    const numberOfTravellers = model.lobData.privateTravel.coverables.sptTravellers.length;
    const excessValue = _.get(model, `lobData.privateTravel.coverables.sptTrips.[${selectedTripIndex}].excessValueExt`);
    const winterSportsAdded = model.lobData.privateTravel.coverables.sptTravellers.some((traveller) => traveller.isWinterSportsNeeded);

    const isAggregator = useMemo(() => {
        return SgSptConstants.AGGS_COVER_LEVEL_CODES.includes(selectedTrip.coverLevel);
    }, [selectedTrip.coverLevel]);

    const coverLevelName = useMemo(() => {
        return isAggregator ? SgSptConstants.COVER_LEVELS.SELECT : selectedTrip.coverLevel;
    }, [selectedTrip, isAggregator]);

    const selectedRegion = useMemo(() => {
        if (!isSelectedTripSingle) {
            const { region } = model.lobData.privateTravel.coverables.sptTrips[selectedTripIndex];
            if (region === SgSptConstants.REGIONS.WORLDWIDE_EXCL) {
                return translator(messages.worldwideExcluding);
            } if (region === SgSptConstants.REGIONS.WORLDWIDE_INCL) {
                return translator(messages.worldwideIncluding);
            }
        }
        return translator(messages.europe);
    }, [model, selectedTripIndex]);

    const getDestination = useMemo(() => {
        const countriesSelected = [];
        if (isSelectedTripSingle) {
            return model.lobData.privateTravel.coverables.sptTrips[selectedTripIndex].sptTripDestinations.map((item) => translator({
                id: `typekey.SPTDestination.${item.destination}`,
                defaultMessage: ''
            }));
        }
        return countriesSelected;
    }, [isSelectedTripSingle, selectedTripIndex]);

    const getAllConditions = useMemo(() => {
        const allConditions = {};
        allConditions.singleTrip = isSelectedTripSingle;
        allConditions.annualTrip = !isSelectedTripSingle;
        allConditions.directQuote = !isAggregator;
        allConditions.aggQuote = isAggregator;
        allConditions.noCancellationOnDirectNotEssential = !isAggregator && !cancellationCoverNeeded
            && coverLevelName !== SgSptConstants.COVER_LEVELS.ESSENTIAL;
        allConditions.essentialCoverLevelNoCancellation = !isAggregator && !cancellationCoverNeeded
            && coverLevelName === SgSptConstants.COVER_LEVELS.ESSENTIAL;
        allConditions.essentialCoverLevelWithCancellation = !isAggregator && cancellationCoverNeeded
            && coverLevelName === SgSptConstants.COVER_LEVELS.ESSENTIAL;
        allConditions.directQuoteNotEssential = !isAggregator && coverLevelName !== SgSptConstants.COVER_LEVELS.ESSENTIAL;
        allConditions.cancellationIncluded = cancellationCoverNeeded || !isSelectedTripSingle;
        allConditions.winterSportsIncluded = winterSportsAdded;
        allConditions.standardOrPlusCover = coverLevelName === SgSptConstants.COVER_LEVELS.STANDARD
            || coverLevelName === SgSptConstants.COVER_LEVELS.PLUS;
        allConditions.notEssentialCover = coverLevelName !== SgSptConstants.COVER_LEVELS.ESSENTIAL;
        allConditions.essentialCoverLevel = coverLevelName === SgSptConstants.COVER_LEVELS.ESSENTIAL;
        allConditions.excessOrAgg = isAggregator || excessValue !== SgSptConstants.EXCESS_CODES.EXCESS_0;
        allConditions.showCruiseBenefits = !isAggregator && coverLevelName === SgSptConstants.COVER_LEVELS.PLUS;
        return allConditions;
    }, [isSelectedTripSingle, isAggregator, cancellationCoverNeeded, winterSportsAdded, coverLevelName, excessValue]);

    const conditionsApply = (covers, item) => {
        const conditions = [];
        if (item.condition) {
            conditions.push(covers[item.condition]);
        }
        if (item.productType) {
            conditions.push(covers[item.productType]);
        }
        if (item.coverLevel) {
            conditions.push(covers[item.coverLevel]);
        }
        return _.every(conditions, Boolean);
    };

    const baggageAmountMsg = useMemo(() => {
        if (isSelectedTripSingle) {
            if (coverLevelName === SgSptConstants.COVER_LEVELS.ESSENTIAL) {
                return translator(messages.baggageAmountEssential);
            }
            if (coverLevelName === SgSptConstants.COVER_LEVELS.STANDARD) {
                return translator(messages.baggageAmountStandard);
            }
            if (coverLevelName === SgSptConstants.COVER_LEVELS.PLUS) {
                return translator(messages.baggageAmountPlus);
            }
        }
        if (coverLevelName === SgSptConstants.COVER_LEVELS.STANDARD) {
            return translator(messages.baggageAmountStandardAnnual);
        }
        if (coverLevelName === SgSptConstants.COVER_LEVELS.SELECT) {
            return translator(messages.baggageAmountSelect);
        }
        return translator(messages.baggageAmountPlusAnnual);
    }, [isSelectedTripSingle, coverLevelName]);

    const winterSportsMedicalExpensesMsg = useMemo(() => {
        if (isSelectedTripSingle) {
            if (coverLevelName === SgSptConstants.COVER_LEVELS.ESSENTIAL) {
                return translator(messages.WsMedicalSingleEssential);
            }
            if (coverLevelName === SgSptConstants.COVER_LEVELS.STANDARD) {
                return translator(messages.WsMedicalSingleStandard);
            }
            if (coverLevelName === SgSptConstants.COVER_LEVELS.PLUS) {
                return translator(messages.WsMedicalSinglePlus);
            }
        }
        if (coverLevelName === SgSptConstants.COVER_LEVELS.STANDARD) {
            return translator(messages.WsMedicalAnnualStandard);
        }
        if (coverLevelName === SgSptConstants.COVER_LEVELS.PLUS) {
            return translator(messages.WsMedicalAnnualPlus);
        }
        return '';
    }, [isSelectedTripSingle, coverLevelName]);

    const getAllParamValues = useMemo(() => {
        const paramValues = {};
        const coverLevelNameParam = isSelectedTripSingle ? coverLevelName.toUpperCase() : `${coverLevelName.toUpperCase()}_ANNUAL`;
        switch (coverLevelName) {
            case SgSptConstants.COVER_LEVELS.ESSENTIAL:
                paramValues.CancellationAmount = tripCostValue > SgSptConstants.SINGLE_TRIP_CANCELLATION_LIMITS.essential
                    ? SgSptConstants.CANCELLATION_AMOUNT.ESSENTIAL
                    : SgNumberUtil.addThousandsCommaSeparator(tripCostValue);
                break;
            case SgSptConstants.COVER_LEVELS.STANDARD:
                paramValues.CancellationAmount = SgSptConstants.CANCELLATION_AMOUNT.STANDARD_ANNUAL;
                if (isSelectedTripSingle) {
                    paramValues.CancellationAmount = tripCostValue * numberOfTravellers > SgSptConstants.SINGLE_TRIP_CANCELLATION_LIMITS.standard
                        ? SgSptConstants.CANCELLATION_AMOUNT.STANDARD
                        : SgNumberUtil.addThousandsCommaSeparator(tripCostValue);
                }
                break;
            case SgSptConstants.COVER_LEVELS.PLUS:
                paramValues.CancellationAmount = SgSptConstants.CANCELLATION_AMOUNT.PLUS_ANNUAL;
                if (isSelectedTripSingle) {
                    paramValues.CancellationAmount = SgNumberUtil.addThousandsCommaSeparator(tripCostValue);
                }
                break;
            case SgSptConstants.COVER_LEVELS.SELECT:
                paramValues.CancellationAmount = SgSptConstants.CANCELLATION_AMOUNT.ESSENTIAL;
                break;
            default:
        }
        paramValues.LostStolenDamagedBaggage = baggageAmountMsg;
        paramValues.WinterSportsMedicalExpenses = winterSportsMedicalExpensesMsg;
        paramValues.DelayedDepartureAmount = SgSptConstants.DELAYED_DEPARTURE[coverLevelNameParam];
        paramValues.PassportAmount = SgSptConstants.PASSPORT_AMOUNT[coverLevelNameParam];
        paramValues.PersonalMoney = SgSptConstants.PERSONAL_MONEY_AMOUNT[coverLevelNameParam];
        paramValues.PersonalLiability = SgSptConstants.PERSONAL_LIABILITY_AMOUNT[coverLevelNameParam];
        paramValues.MedicalAmount = SgSptConstants.MEDICAL_EXPENSES_AMOUNT[coverLevelNameParam];
        paramValues.DentalEmergencyAmount = SgSptConstants.DENTAL_EMERGENCY[coverLevelNameParam];
        paramValues.AbandonmentAmount = SgSptConstants.ABANDONMENT_AMOUNT[coverLevelNameParam];
        paramValues.MissedDepartureAmount = SgSptConstants.MISSED_DEPARTURE_AMOUNT[coverLevelNameParam];
        paramValues.DelayedFlightAssistanceTime = SgSptConstants.DELAYED_FLIGHT[coverLevelNameParam];
        paramValues.PetCareAmount = SgSptConstants.PET_CARE[coverLevelNameParam];
        paramValues.DelayedBaggageAmount = SgSptConstants.DELAYED_BAGGAGE[coverLevelNameParam];
        paramValues.CriminalInjuryAmount = SgSptConstants.CRIMINAL_INJURY[coverLevelNameParam];
        paramValues.HospitalBenefitAmount = SgSptConstants.HOSPITAL_BENEFIT[coverLevelNameParam];
        paramValues.EnforcedStay = SgSptConstants.ENFORCED_STAY_AMOUNT[coverLevelNameParam];
        paramValues.PersonalAccident = SgSptConstants.PERSONAL_ACCIDENT_AMOUNT[coverLevelNameParam];
        paramValues.DeathBenefit = SgSptConstants.PA_DEATH_BENEFIT[coverLevelNameParam];
        paramValues.SubstituteAccommodation = SgSptConstants.SUBSTITUTE_ACCOMMODATION_AMOUNT[coverLevelNameParam];
        return paramValues;
    }, [baggageAmountMsg, winterSportsMedicalExpensesMsg, coverLevelName, isSelectedTripSingle, numberOfTravellers, tripCostValue]);

    const messageParams = useMemo(() => {
        const {
            CancellationAmount,
            LostStolenDamagedBaggage,
            WinterSportsMedicalExpenses,
            DelayedDepartureAmount,
            PassportAmount,
            PersonalMoney,
            PersonalLiability,
            DentalEmergencyAmount,
            DelayedFlightAssistanceTime,
            CriminalInjuryAmount,
            MedicalAmount,
            PetCareAmount,
            DelayedBaggageAmount,
            AbandonmentAmount,
            MissedDepartureAmount,
            PersonalAccident,
            HospitalBenefitAmount,
            DeathBenefit,
            SubstituteAccommodation,
            EnforcedStay,
        } = getAllParamValues;

        return {
            cancellationAmount: CancellationAmount,
            lostStolenDamagedBaggage: LostStolenDamagedBaggage,
            winterSportsMedicalExpenses: WinterSportsMedicalExpenses,
            passportAmount: PassportAmount,
            delayedDepartureAmount: DelayedDepartureAmount,
            personalMoney: PersonalMoney,
            personalLiability: PersonalLiability,
            dentalEmergencyAmount: DentalEmergencyAmount,
            hospitalBenefitAmount: HospitalBenefitAmount,
            delayedFlightAssistanceTime: DelayedFlightAssistanceTime,
            medicalAmount: MedicalAmount,
            petCareAmount: PetCareAmount,
            delayedBaggageAmount: DelayedBaggageAmount,
            criminalInjuryAmount: CriminalInjuryAmount,
            abandonmentAmount: AbandonmentAmount,
            missedDepartureAmount: MissedDepartureAmount,
            enforcedStay: EnforcedStay,
            personalAccident: PersonalAccident,
            deathBenefit: DeathBenefit,
            substituteAccommodation: SubstituteAccommodation,
        };
    }, [getAllParamValues]);

    const getMessages = useCallback((cover, product, type) => {
        const productConfig = IpidConfig[product];
        return SgIpidUtil.getMessages(cover, product, type, productConfig, conditionsApply, messages, messageParams, translator);
    }, [translator]);

    const benefitLimitsMsg = useMemo(() => {
        if (isSelectedTripSingle) {
            if (selectedTrip.coverLevel === SgSptConstants.COVER_LEVELS.ESSENTIAL) {
                return translator(messages.benefitLimitsPerTripNotPerPerson);
            }
            if (selectedTrip.coverLevel === SgSptConstants.COVER_LEVELS.STANDARD || selectedTrip.coverLevel === SgSptConstants.COVER_LEVELS.PLUS) {
                return translator(messages.benefitLimitsPerPerson);
            }
            return translator(messages.benefitLimitsPerPerson);
        }
        return translator(messages.benefitLimitsPerPersonPerTrip);
    }, [isSelectedTripSingle]);

    const initCommonData = useCallback((key) => {
        let productType = '';
        if (isSelectedTripSingle) {
            productType = translator(messages.ProductSingleTripTravelInsurance);
        } else {
            productType = translator(messages.ProductAnnualTripTravelInsurance);
        }
        const productName = `${coverLevelName} ${productType}`;

        return {
            insuredList: getMessages(getAllConditions, key, `${CommonConstants.IPID_PAGE.Insured}`),
            notInsuredList: getMessages(getAllConditions, key, `${CommonConstants.IPID_PAGE.NotInsured}`),
            restrictionsList: getMessages(getAllConditions, key, `${CommonConstants.IPID_PAGE.Restrictions}`),
            coveredList: [],
            obligationsList: [],
            header: {
                title: translator(messages.Titlespt),
                suffix: key,
                product: productName.charAt(0).toUpperCase() + productName.slice(1),
                insuranceTypeDesc: translator(messages.WhatIsTheTypeOfInsurance, {
                    benefitLimits: benefitLimitsMsg
                })
            },
            date: {
                periodStartDate: SgDateUtil.toLongDate(coverStartDate),
                periodEndDate: isSelectedTripSingle ? SgDateUtil.toLongDate(returnDateDtoSingle) : SgDateUtil.toLongDate(coverEndDateAnnual)
            },
            phoneNumber: translator(SgSptConstants.PHONE_NUMBER_CUSTOMER_SERVICE)
        };
    }, [isSelectedTripSingle, getMessages, getAllConditions, translator, coverStartDate, returnDateDtoSingle,
        coverEndDateAnnual, coverLevelName, benefitLimitsMsg]);

    const coverLevelMessages = useMemo(() => {
        return {
            commonObligations: ['MyObligations1', 'MyObligations2', 'MyObligations3', 'MyObligations4', 'MyObligations5'],
        };
    }, []);

    const initIpidData = useMemo(() => {
        const data = initCommonData(SgSptConstants.SPT);
        data.obligationsList = SgIpidUtil.createListFromMessages(coverLevelMessages.commonObligations, null, messages, translator);
        data.coveredList = isSelectedTripSingle
            ? [getDestination.toString().replace(/,/g, ', ') + translator(messages.PlusOtherCountries)] : [selectedRegion];
        data.howDoIPay = translator(messages.WhenAndHowDoIPay);
        data.hideCloseButton = false;
        data.CompanyDetails = translator(messages.LegalDisclaim);
        data.Product = CommonConstants.TRAVEL_PRODUCT_CODE;
        return data;
    }, [initCommonData,
        SgIpidUtil.createListFromMessages]);

    const overrides = {
        sptIpid: {
            data: initIpidData
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgIpidDetails
        },
    };

    return (
        <div>
            {renderContentFromMetadata(metadata.componentContent, overrides, resolvers)}
        </div>
    );
}

export default SgSptIpid;
